import { type ComponentProps, type ReactNode, useId, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from "lucide-react";

import { Button } from "../button";
import { Input } from "../input";
import { Label } from "../label";
import { cn } from "@/lib/utils";
import {
  authInputClass,
  authPrimaryButtonClass,
  passwordRules,
  scorePassword,
  strengthBarClass,
  strengthLabel,
} from "./auth-tokens";

/**
 * Shared chrome for every authentication form.
 *
 * Three apps render the same four forms (app, www, admin), so the visual
 * vocabulary lives here once instead of being re-authored per screen:
 *
 *  - **Serif is for subject matter.** The form title uses `font-display`
 *    (Fraunces) because it is the one piece of editorial content on the
 *    screen; every label, hint and button below it is sans.
 *  - **Warm paper, cool action.** Field surfaces are stone; the one gradient
 *    CTA (primary → secondary) is the only saturated element on the form.
 *  - **One primary action per form.** Google is deliberately outline: it is
 *    an alternative, not a co-equal call to action.
 *
 * Surfaces and the strength scale live in `auth-tokens.ts` — this file
 * exports components only.
 */

/* ── Heading ────────────────────────────────────────────────── */

export interface AuthHeadingProps {
  title: string;
  description?: string;
  /** Small mark above the title — an icon in a tinted square. */
  icon?: ReactNode;
  className?: string;
}

export const AuthHeading = ({ title, description, icon, className }: AuthHeadingProps) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
    className={cn("text-center", className)}
  >
    {icon ? (
      <div className="mb-5 flex justify-center">
        <div className="relative">
          <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 blur-lg" />
          <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-stone-200/80 bg-white shadow-sm dark:border-stone-800 dark:bg-stone-900">
            {icon}
          </div>
        </div>
      </div>
    ) : null}
    <h1 className="font-display text-[26px] font-semibold leading-tight tracking-tight text-stone-900 dark:text-stone-50">
      {title}
    </h1>
    {description ? (
      <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-stone-500 dark:text-stone-400">
        {description}
      </p>
    ) : null}
  </motion.div>
);

/* ── Fields ─────────────────────────────────────────────────── */

export interface AuthFieldProps {
  label: string;
  icon: ReactNode;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  /** Rendered on the label row, right-aligned ("Forgot password?"). */
  action?: ReactNode;
  /** Quiet line under the input. */
  hint?: ReactNode;
  /** Trailing slot inside the input shell — used for the reveal toggle. */
  trailing?: ReactNode;
  /** Tints the shell destructive and renders the message under the field. */
  error?: string | null;
  name?: string;
}

export const AuthField = ({
  label,
  icon,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  action,
  hint,
  trailing,
  error,
  name,
}: AuthFieldProps) => {
  const autoId = useId();

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label
          htmlFor={name ?? autoId}
          className="text-[13px] font-medium text-stone-700 dark:text-stone-300"
        >
          {label}
        </Label>
        {action}
      </div>

      <div className="group relative">
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 left-3.5 flex items-center transition-colors duration-200",
            "text-stone-400 group-focus-within:text-primary",
            error && "text-destructive",
          )}
        >
          {icon}
        </div>
        <Input
          id={name ?? autoId}
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={error ? true : undefined}
          className={cn(
            authInputClass,
            trailing ? "pr-11" : "pr-3.5",
            error && "border-destructive/60 focus:border-destructive/60 focus:ring-destructive/10",
          )}
        />
        {trailing ? (
          <div className="absolute inset-y-0 right-1.5 flex items-center">{trailing}</div>
        ) : null}
      </div>

      <AnimatePresence initial={false} mode="wait">
        {error ? (
          <motion.p
            key="error"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="flex items-start gap-1.5 text-[13px] font-medium text-destructive"
            role="alert"
          >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {error}
          </motion.p>
        ) : hint ? (
          <motion.p
            key="hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="text-xs text-stone-500 dark:text-stone-400"
          >
            {hint}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

/** Shared "Show / Hide password" control. `aria-label` carries the word
 *  "password" so assistive tech and the e2e suite both find it. */
export const PasswordReveal = ({
  visible,
  onToggle,
  autoFocusLabel,
  showLabel = "Show password",
  hideLabel = "Hide password",
}: {
  visible: boolean;
  onToggle: () => void;
  autoFocusLabel?: boolean;
  showLabel?: string;
  hideLabel?: string;
}) => (
  <button
    type="button"
    onClick={onToggle}
    tabIndex={autoFocusLabel ? -1 : 0}
    aria-label={visible ? hideLabel : showLabel}
    aria-pressed={visible}
    className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-stone-800 dark:hover:text-stone-200"
  >
    {visible ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
  </button>
);

/** Convenience wrapper: a password field with the reveal toggle wired up. */
export const AuthPasswordField = ({
  value,
  onChange,
  label = "Password",
  name,
  autoComplete = "current-password",
  placeholder = "••••••••",
  required,
  hint,
  error,
  action,
  showPasswordLabel,
  hidePasswordLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  name?: string;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string | null;
  /** Rendered on the label row, right-aligned. */
  action?: ReactNode;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <AuthField
      label={label}
      name={name}
      icon={<LockGlyph />}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
      hint={hint}
      error={error}
      action={action}
      trailing={
        <PasswordReveal
          visible={visible}
          onToggle={() => setVisible((v) => !v)}
          showLabel={showPasswordLabel}
          hideLabel={hidePasswordLabel}
        />
      }
    />
  );
};

const LockGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-[18px] w-[18px]"
    aria-hidden="true"
  >
    <rect x="3.5" y="10.5" width="17" height="10" rx="2.5" />
    <path d="M7.75 10.5V7.25a4.25 4.25 0 1 1 8.5 0v3.25" />
  </svg>
);

/* ── Strength meter ─────────────────────────────────────────── */

export const PasswordStrength = ({ password, labels }: { password: string; labels?: string[] }) => {
  // `labels[0]` is the empty state, so a filled score reads labels[1..4].
  const score = useMemo(() => scorePassword(password), [password]);

  return (
    <motion.div
      initial={false}
      animate={{ opacity: password ? 1 : 0.45 }}
      transition={{ duration: 0.25 }}
      className="flex items-center gap-3 pt-0.5"
    >
      <div className="flex flex-1 gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((step) => (
          <div key={step} className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-800">
            <motion.div
              initial={false}
              animate={{ scaleX: score >= step ? 1 : 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: step * 0.04 }}
              className={cn("h-full w-full origin-left rounded-full", strengthBarClass[score])}
            />
          </div>
        ))}
      </div>
      <span
        className={cn(
          "w-16 shrink-0 text-right text-[11px] font-semibold uppercase tracking-wider transition-colors",
          score >= 3
            ? "text-emerald-600 dark:text-emerald-400"
            : score === 2
              ? "text-amber-600 dark:text-amber-400"
              : score === 1
                ? "text-red-600 dark:text-red-400"
                : "text-stone-400",
        )}
      >
        {labels?.[score] ?? (strengthLabel[score] || "Strength")}
      </span>
    </motion.div>
  );
};

/** Animated requirement ticks under the signup password field. */
export const PasswordChecklist = ({ password, labels }: { password: string; labels?: string[] }) => {
  // Labels are injected rather than read from i18next here: this package is
  // shared with the admin panel, which deliberately stays English.
  const rules = passwordRules.map((rule, i) => ({ label: labels?.[i] ?? rule.label, met: rule.test(password) }));

  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
      {rules.map((rule) => (
        <li
          key={rule.label}
          className={cn(
            "flex items-center gap-1.5 text-xs transition-colors duration-200",
            rule.met ? "text-emerald-600 dark:text-emerald-400" : "text-stone-400 dark:text-stone-500",
          )}
        >
          <span
            className={cn(
              "flex h-3.5 w-3.5 items-center justify-center rounded-full transition-colors duration-200",
              rule.met ? "bg-emerald-500/15" : "bg-stone-200 dark:bg-stone-800",
            )}
          >
            <Check
              className={cn(
                "h-2.5 w-2.5 transition-all duration-200",
                rule.met ? "scale-100 opacity-100" : "scale-50 opacity-0",
              )}
            />
          </span>
          {rule.label}
        </li>
      ))}
    </ul>
  );
};

/* ── Actions ────────────────────────────────────────────────── */

export const AuthPrimaryButton = ({
  loading,
  loadingLabel,
  children,
  className,
  ...props
}: {
  loading?: boolean;
  loadingLabel?: string;
  children: ReactNode;
  className?: string;
} & Omit<ComponentProps<"button">, "children">) => (
  <Button type="submit" disabled={loading} className={cn(authPrimaryButtonClass, className)} {...props}>
    {/* Sweep — a single highlight travelling across the gradient on hover. */}
    <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
    <span className="relative flex items-center justify-center gap-2">
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {loadingLabel ?? children}
        </>
      ) : (
        children
      )}
    </span>
  </Button>
);

export const GoogleIcon = ({ className }: { className?: string } = {}) => (
  <svg className={cn("h-[18px] w-[18px]", className)} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

/** Form-level error, anchored above the primary action where it can't be
 *  mistaken for a field-level message. */
export const AuthAlert = ({ message }: { message?: string | null }) => (
  <AnimatePresence initial={false}>
    {message ? (
      <motion.div
        key={message}
        initial={{ opacity: 0, y: -6, height: 0 }}
        animate={{ opacity: 1, y: 0, height: "auto" }}
        exit={{ opacity: 0, y: -6, height: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-xl border border-destructive/25 bg-destructive/[0.06] px-3.5 py-3 text-[13px] font-medium leading-relaxed text-destructive"
        >
          <AlertCircle className="mt-px h-4 w-4 shrink-0" />
          {message}
        </div>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

export const AuthDivider = ({ label = "or continue with" }: { label?: string }) => (
  <div className="relative py-0.5" aria-hidden="true">
    <div className="absolute inset-0 flex items-center">
      <span className="w-full border-t border-stone-200/80 dark:border-stone-800" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-white/80 px-2.5 text-[11px] font-medium uppercase tracking-wider text-stone-400 backdrop-blur-sm dark:bg-stone-900/80">
        {label}
      </span>
    </div>
  </div>
);

export const GoogleSignInButton = ({
  loading,
  onClick,
  label = "Continue with Google",
  className,
}: {
  loading?: boolean;
  onClick: () => void;
  label?: string;
  className?: string;
}) => (
  <Button
    type="button"
    variant="outline"
    onClick={onClick}
    disabled={loading}
    className={cn(
      "h-11 w-full rounded-xl border-stone-200/90 bg-white text-[15px] font-medium text-stone-700 shadow-sm transition-all duration-200 hover:border-stone-300 hover:bg-stone-50 hover:shadow-md active:scale-[0.985] dark:border-stone-800 dark:bg-stone-900/60 dark:text-stone-200 dark:hover:bg-stone-900",
      className,
    )}
  >
    {loading ? <Loader2 className="mr-2 h-[18px] w-[18px] animate-spin" /> : <GoogleIcon className="mr-2" />}
    {label}
  </Button>
);

/** Quiet legal / security line at the foot of a form. */
export const AuthFootnote = ({ children }: { children: ReactNode }) => (
  <p className="text-center text-xs leading-relaxed text-stone-400 dark:text-stone-500">{children}</p>
);